const XLSX = require('@sheet/core')
const { OUTBOUND_TEMPLATE_COLUMNS, OUTBOUND_SHIPMENT_TEMPLATE_HEADERS } = require('./constants')
/**
 * Converts an Excel outbound shipment file to CSV format that AGAVE expects
 *
 * @param {Buffer|ArrayBuffer} fileBuffer - The Excel file buffer
 * @param {string} fileName - Original file name
 * @returns {Object} - { csvContent: string, csvFileName: string }
 */
const convertExcelOutboundShipmentToCsv = (state, fileBuffer, fileName = 'shipment.xlsx') => {
  const { logger } = state || {}
  try {
    const workbook = XLSX.read(fileBuffer, { type: fileBuffer instanceof Buffer ? 'buffer' : 'array', cellDates: true })
    // Get the "Outbound" sheet or first sheet
    const sheetName = workbook.SheetNames.includes('Outbound')
      ? 'Outbound'
      : workbook.SheetNames[0]

    if (!sheetName) {
      throw new Error('No sheets found in Excel file')
    }
    const worksheet = workbook.Sheets[sheetName]

    validateExcelStructure(worksheet)

    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
      raw: false, // Get formatted strings, not raw values
      defval: '' // Default value for empty cells
    })

    if (!jsonData || jsonData.length === 0) {
      throw new Error('No data found in Excel sheet')
    }

    const getVal = (row, key) => row[key] || ''

    const mappedData = jsonData.map(row => {
      const baseMap = OUTBOUND_SHIPMENT_TEMPLATE_HEADERS.AGAVE_CSV.reduce((acc, column) => {
        // Exclude the 'Product' column as it requires special handling (tolowerCase and different source)
        if (column !== OUTBOUND_TEMPLATE_COLUMNS.PRODUCT) {
          acc[column] = getVal(row, column)
        }
        return acc
      }, {})
      baseMap[OUTBOUND_TEMPLATE_COLUMNS.PRODUCT] = getVal(row, OUTBOUND_TEMPLATE_COLUMNS.PRODUCT_CODE).toLowerCase()

      return baseMap
    })

    const newWorksheet = XLSX.utils.json_to_sheet(mappedData, {
      header: OUTBOUND_SHIPMENT_TEMPLATE_HEADERS.AGAVE_CSV
    })

    const csvContent = XLSX.utils.sheet_to_csv(newWorksheet, {
      FS: ',', // Field separator (comma)
      RS: '\n' // Record separator (newline)
    })

    const csvFileName = fileName.replace(/\.(xlsx|xls)$/i, '.csv')

    return {
      csvContent,
      csvFileName
    }
  } catch (error) {
    if (logger) {
      logger.error(`Failed to convert outshipment excel template to CSV: ${error}`)
    }
    throw new Error(`Failed to convert outshipment excel template to CSV: ${error}`)
  }
}

const validateExcelStructure = (worksheet) => {
  const expectedHeaders = OUTBOUND_SHIPMENT_TEMPLATE_HEADERS.EXCEL_TEMPLATE
  const actualHeaders = []

  // Get headers from first row
  const range = XLSX.utils.decode_range(worksheet['!ref'])
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col })
    const cell = worksheet[cellAddress]
    if (cell && cell.v) {
      actualHeaders.push(cell.v.toString().trim())
    }
  }

  // Check if all expected headers are present
  const missingHeaders = expectedHeaders.filter(header =>
    !actualHeaders.includes(header)
  )

  if (missingHeaders.length > 0) {
    throw new Error(`Invalid Excel template. Missing required columns: ${missingHeaders.join(', ')}. Please use the provided template.`)
  }

  return true
}

module.exports = {
  convertExcelOutboundShipmentToCsv
}
